// BannerStack — единый контейнер всех верхних pill-баннеров (offline,
// impersonation, past-due, trial). Раньше каждый баннер рендерился как
// `sticky top-0 z-{30..50}` siblings ДО MainLayout и конфликтовал с
// `fixed` Topbar: на mobile они перекрывались топбаром, а при нескольких
// активных баннерах штабелировались у top-0 с конфликтующими z (визуальный
// коллапс — critical-находка аудита).
//
// Теперь: один `fixed` контейнер ровно под топбаром (top = --topbar-height,
// в которую уже включён safe-area-top). Баннеры — обычные блоки в потоке,
// стопкой друг под другом. Высота стопки публикуется в --banners-height на
// <html>, и <main> резервирует её в padding-top (контент не уезжает под
// баннеры). Desktop-карточка TrialBanner (`fixed bottom-right`) — out of
// flow, в высоту стопки не входит (ResizeObserver меряет только поток).

import { type ReactNode, useLayoutEffect, useRef } from "react";

export function BannerStack({ children }: { children: ReactNode }): React.ReactElement {
	const ref = useRef<HTMLDivElement | null>(null);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		const publish = (): void =>
			document.documentElement.style.setProperty("--banners-height", `${el.offsetHeight}px`);
		publish();
		const observer = new ResizeObserver(publish);
		observer.observe(el);
		return () => {
			observer.disconnect();
			document.documentElement.style.setProperty("--banners-height", "0px");
		};
	}, []);

	return (
		<div
			ref={ref}
			// Safe-area: отступ висит на ПЕРВОМ баннере, а не на контейнере.
			// Контейнер смонтирован всегда, и собственный `padding-top` он занимал бы
			// и без баннеров — на полноэкранных экранах (воркспейс курса, где
			// `--topbar-height` = 0) это давало бы полосу чужого фона над шапкой.
			// У баннера фон свой, поэтому именно он и обязан закрасить notch.
			// Величина — дефицит: сколько inset'а НЕ закрыл топбар (с ним она 0).
			//
			// Нижняя граница — СОБСТВЕННЫЙ верхний паддинг баннера (`--sh-banner-pt`,
			// по умолчанию `py-2`). Без неё правило перезаписывало чужой `py-*`
			// нулём везде, где дефицита нет, то есть на всём вебе: у баннера
			// оставался только нижний отступ, надпись прижималась к верхней кромке
			// и стояла выше центра своей полосы. Баннеры с другим `py` объявляют
			// `--sh-banner-pt` сами (см. impersonation / past-due / trial).
			className="fixed top-[var(--topbar-height,0px)] z-20 flex flex-col transition-[left,right] duration-base ease-out [&>*]:animate-drop-in [&>*:first-child]:pt-[max(var(--sh-banner-pt,0.5rem),calc(env(safe-area-inset-top,0px)-var(--topbar-height,0px)))]"
			// Стопка занимает КОНТЕНТНУЮ область, а не всё окно: слева вычитаем
			// рельс навигации, справа — открытую боковую панель. Иначе текст,
			// центрированный внутри баннера, уезжал влево относительно того, что
			// пользователь видит как «страницу». Та же геометрия, что у Topbar
			// (left/right по --sidebar-width / --side-panel-width), поэтому полоска
			// точно продолжает шапку.
			//
			// Переменные ставит MainLayout на <html>; на overlay-routes и mobile их
			// нет либо они 0 → баннер во всю ширину, как раньше.
			// --tutor-push-width — тот же отжим на overlay-routes, где MainLayout не
			// смонтирован; max() берёт актуальный из двух, они не суммируются.
			style={{
				left: "var(--sidebar-width, 0px)",
				right: "max(var(--side-panel-width, 0px), var(--tutor-push-width, 0px))",
			}}
		>
			{children}
		</div>
	);
}
